import type { AssetCollection } from '../assets/api.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { openAssetImagePicker } from './assetImagePicker.js';

export type EntityImageHandlers = {
  uploadFromPc: (file: File) => Promise<unknown>;
  assignFromLibrary: (publicPath: string) => Promise<unknown>;
};

export function mountEntityImageBlock(
  section: HTMLElement,
  options: {
    entityId: number;
    collection: AssetCollection;
    imagePath?: string | null;
    alt: string;
    pickerTitle: string;
    handlers: EntityImageHandlers;
    onAfterSave: (saved: unknown) => void;
    /** Si fourni, monte le bloc ici au lieu du header détail. */
    mountIn?: HTMLElement;
  }
): void {
  const imageUrl = resolveMediaUrl(options.imagePath);
  const block = document.createElement('div');
  block.className = 'entity-image-block car-detail-image-block';
  block.innerHTML = `
    <div class="car-detail-image-main">
      ${
        imageUrl
          ? `<img src="" alt="${options.alt}" class="car-detail-image-large" />`
          : '<span class="car-detail-image-placeholder">Aucune image</span>'
      }
    </div>
    <div class="entity-image-actions car-detail-image-actions">
      <label class="entity-image-file-label">
        <span class="btn btn-secondary btn-sm">Depuis mon PC</span>
        <input type="file" accept="image/png,image/jpeg,image/webp" class="entity-image-file-input" hidden />
      </label>
      <button type="button" class="btn btn-primary btn-sm entity-image-assets-btn">Depuis la bibliothèque</button>
    </div>
    <p class="car-detail-image-hint">PNG, JPEG ou WebP — depuis votre ordinateur ou la bibliothèque de visuels.</p>
    <p class="car-detail-image-confirm" role="status" aria-live="polite"></p>
  `;

  const img = block.querySelector<HTMLImageElement>('.car-detail-image-large');
  if (img && imageUrl) {
    img.src = imageUrl;
    img.alt = options.alt;
  }

  const fileInput = block.querySelector('.entity-image-file-input') as HTMLInputElement;
  const assetsBtn = block.querySelector('.entity-image-assets-btn') as HTMLButtonElement;
  const confirmEl = block.querySelector('.car-detail-image-confirm') as HTMLElement;

  const setBusy = (busy: boolean): void => {
    if (fileInput) fileInput.disabled = busy;
    if (assetsBtn) assetsBtn.disabled = busy;
  };

  const showMsg = (text: string, ok = false): void => {
    if (!confirmEl) return;
    confirmEl.textContent = text;
    confirmEl.classList.toggle('car-detail-image-confirm--ok', ok);
  };

  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    setBusy(true);
    showMsg('Envoi en cours…');
    void options.handlers
      .uploadFromPc(file)
      .then((saved) => {
        showMsg('Image enregistrée.', true);
        options.onAfterSave(saved);
      })
      .catch((err: Error) => {
        showMsg(err.message || "Échec de l'envoi.");
      })
      .finally(() => {
        setBusy(false);
        fileInput.value = '';
      });
  });

  assetsBtn?.addEventListener('click', () => {
    openAssetImagePicker({
      collection: options.collection,
      title: options.pickerTitle,
      onSelect: (item) => {
        setBusy(true);
        showMsg('Application…');
        void options.handlers
          .assignFromLibrary(item.publicPath)
          .then((saved) => {
            showMsg('Visuel de la bibliothèque appliqué.', true);
            options.onAfterSave(saved);
          })
          .catch((err: Error) => {
            showMsg(err.message || "Échec de l'assignation.");
          })
          .finally(() => setBusy(false));
      },
      onError: (msg) => showMsg(msg),
    });
  });

  if (options.mountIn) {
    options.mountIn.appendChild(block);
    return;
  }

  const header = section.querySelector('.car-detail-header, .circuits-detail-header');
  if (header && !header.querySelector('.entity-image-block')) {
    header.appendChild(block);
  } else {
    section.prepend(block);
  }
}
